import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Space, Table, InputNumber, Modal } from "antd";
import "./add_line_wh_transfer_request.css";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType, TableProps } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import axios from "axios";

interface DataType {
    key: string;
    item_code: string;
    item_name: string;
    uom: string;
    qty: number;
    location_code_rack?: string;
}

interface AddLineProps {
    originWarehouse: string;
    onOk: (selectedItems: DataType[]) => void;
    onCancel: () => void;
}

type DataIndex = keyof DataType;

const AddLineWhTransferRequest: React.FC<AddLineProps> = ({
    originWarehouse,
    onOk,
    onCancel
}) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [sparePartsData, setSparePartsData] = useState<DataType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isQtyInputVisible, setIsQtyInputVisible] = useState(false);
    const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
    const [selectedItemsForQty, setSelectedItemsForQty] = useState<DataType[]>([]);

    useEffect(() => {
        const fetchSpareParts = async () => {
            if (!originWarehouse) {
                setSparePartsData([]);
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/spareparts-master`, {
                    params: {
                        warehouse: originWarehouse
                    }
                }
                );

                const mappedData = response.data.map((item: any) => ({
                    ...item,
                    key: `${item.item_code}_${item.location_code_rack || ''}`,
                }));
                setSparePartsData(mappedData);
            } catch (error) {
                console.error("Error fetching spare parts data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSpareParts();
    }, [originWarehouse]);

    const handleShowQtyInput = () => {
        const selectedItems = sparePartsData.filter(item => selectedRowKeys.includes(item.key));
        if (selectedItems.length === 0) {
            Modal.warning({
                title: 'No Item Selected',
                content: 'Please select at least one item to proceed.',
            });
            return;
        }
        setSelectedItemsForQty(selectedItems);
        const initialQuantities = selectedItems.reduce((acc, item) => {
            acc[item.key] = 0;
            return acc;
        }, {} as { [key: string]: number });
        setQuantities(initialQuantities);
        setIsQtyInputVisible(true);
    };

    const handleQtyChange = (key: string, value: number | null) => {
        setQuantities(prev => ({ ...prev, [key]: value || 0 }));
    };

    const handleConfirmQty = () => {
        const itemsWithQty = selectedItemsForQty.map(item => ({
            ...item,
            qty: quantities[item.key] || 0,
        })).filter(item => item.qty > 0);

        if (itemsWithQty.length === 0) {
            Modal.error({
                title: 'Invalid Quantity',
                content: 'Please enter a quantity greater than 0 for at least one item.',
            });
            return;
        }

        onOk(itemsWithQty);
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex]?.toString() ?? '')
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {

            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            ...getColumnSearchProps('item_code'),
            sorter: (a, b) => a.item_code.localeCompare(b.item_code),
            sortDirections: ['descend', 'ascend'],

        },
        {

            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            ...getColumnSearchProps('item_name'),
            sorter: (a, b) => a.item_name.localeCompare(b.item_name),
            sortDirections: ['descend', 'ascend'],

        },
        {

            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            ...getColumnSearchProps('uom'),
            sorter: (a, b) => a.uom.localeCompare(b.uom),
            sortDirections: ['descend', 'ascend'],

        },
        {

            title: 'Available Qty',
            dataIndex: 'qty',
            key: 'qty',
            ...getColumnSearchProps('qty'),
            sorter: (a, b) => a.qty - b.qty,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Location Rack',
            dataIndex: 'location_code_rack',
            key: 'location_code_rack',
            ...getColumnSearchProps('location_code_rack' as DataIndex),
            sorter: (a, b) => (a.location_code_rack || '').localeCompare(b.location_code_rack || ''),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    const qtyInputColumns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
        },
        {
            title: 'Available Qty',
            dataIndex: 'qty',
            key: 'qty',
        },
        {
            title: 'Location Rack',
            dataIndex: 'location_code_rack',
            key: 'location_code_rack',
        },
        {
            title: 'Request Qty',
            key: 'request_qty',
            render: (_, record) => (
                <InputNumber
                    min={0}
                    max={record.qty}
                    style={{ width: '100%' }}
                    value={quantities[record.key]}
                    onChange={(value) => handleQtyChange(record.key, value)}
                />
            ),
        },
    ];

    const rowSelection: TableProps<DataType>["rowSelection"] = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    };

    return (

        <div id="ieml" style={transformStyle}>
            <div className="gjs-grid-row" id="i275">
                <div className="gjs-grid-column" id="ipth">
                    {!isQtyInputVisible ? (
                        <>
                            <Table<DataType>
                                rowSelection={rowSelection}
                                columns={columns}
                                dataSource={sparePartsData}
                                loading={loading}
                                pagination={{ pageSize: 5 }}
                                scroll={{ y: 240 }}
                                style={{ marginBottom: "20px" }}
                            />

                            <Space style={{ paddingTop: "20px" }}>
                                <Button
                                    type="primary"
                                    onClick={handleShowQtyInput}
                                    style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            style={{ width: "22px", height: "22px" }}>
                                            <path
                                                fillRule="evenodd"
                                                d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    }>
                                    OK
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={onCancel}
                                    style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "red",
                                        color: "white",
                                        border: "none",
                                    }}>
                                    Cancel
                                </Button>
                            </Space>
                        </>
                    ) : (
                        <>
                            <Table<DataType>
                                className="qty-input-table"
                                columns={qtyInputColumns}
                                dataSource={selectedItemsForQty}
                                pagination={{ pageSize: 5 }}
                                scroll={{ y: 240 }}
                                style={{ marginBottom: "20px" }}
                                rowKey="key"
                            />
                            <Space style={{ paddingTop: "20px" }}>
                                <Button
                                    type="primary"
                                    onClick={handleConfirmQty}
                                    style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    Confirm
                                </Button>
                                <Button
                                    onClick={() => setIsQtyInputVisible(false)}
                                    style={{
                                        height: "40px",
                                        width: "120px",
                                    }}
                                >
                                    Back
                                </Button>
                            </Space>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddLineWhTransferRequest;